import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import jwtDecode from "jwt-decode";
import { setAuthTokens, clearAuthTokens } from "../../utils/authTokens";

// decoded token from new auth service
interface DecodedToken {
  sub: string; // auth identity id (standard JWT claim) - canonical identity
  _id?: string; // legacy field, fallback to sub
  backendUserId?: string; // user id in backend (new field)
  userId?: string; // user id in backend (legacy, for compatibility)
  username: string;
  email: string;
  role: string;
  scope?: string; // space-separated scopes
  sessionId?: string; // may not be in access token
  mfaEnabled?: boolean; // may not be in token
  mfaVerified: boolean;
  emailVerified?: boolean;
}

// mfa state for login flow
interface MfaState {
  required: boolean;
  token: string | null;
  methods: string[];
}

export interface AuthState {
  // access token in memory (refresh token is in HttpOnly cookie)
  accessToken: string | null;
  // backward compatibility - points to accessToken
  token: string | null;
  // user info from jwt
  email: string | null;
  username: string | null;
  role: string | null;
  _id: string | null; // auth identity id
  userId: string | null; // backend user id
  sessionId: string | null;
  // mfa state
  mfa: MfaState;
  mfaEnabled: boolean;
  // error state
  error: string | null;
  // loading state
  isLoading: boolean;
  // auth bootstrap lifecycle for page refresh/session restore
  bootstrapStatus: "idle" | "loading" | "ready";
  isAuthenticatedResolved: boolean;
}

// payload for setting tokens from new auth service
interface SetTokensPayload {
  accessToken: string;
  refreshToken?: string; // optional for backward compatibility, but not stored (in HttpOnly cookie)
}

// payload for mfa required response
interface MfaRequiredPayload {
  mfaToken: string;
  mfaMethods: string[];
}

const getInitialState = (): AuthState => {
  // No longer reading from localStorage
  // Tokens will be restored via refresh endpoint on app startup (refresh token in HttpOnly cookie)
  // Initial state is empty - tokens will be loaded from memory after migration
  return {
    accessToken: null,
    token: null,
    email: null,
    username: null,
    role: null,
    _id: null,
    userId: null,
    sessionId: null,
    mfa: {
      required: false,
      token: null,
      methods: [],
    },
    mfaEnabled: false,
    error: null,
    isLoading: false,
    bootstrapStatus: "idle",
    isAuthenticatedResolved: false,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    // set access token (refresh token is in HttpOnly cookie)
    setTokens(state, action: PayloadAction<SetTokensPayload>) {
      const { accessToken } = action.payload;

      // validate token before storing
      if (!accessToken) {
        console.error('[authSlice] setTokens called without accessToken');
        state.error = "Invalid token: missing access token";
        return;
      }

      // store in memory (via authTokens utility)
      // refresh token is stored in HttpOnly cookie by backend, not in JS
      setAuthTokens({ accessToken });

      try {
        const decoded = jwtDecode<DecodedToken>(accessToken);
        // use sub (standard JWT claim) or fallback to _id for legacy tokens
        const identityId = decoded.sub || decoded._id || null;
        // use backendUserId (new) or userId (legacy) or fallback to identityId
        const backendUserId = decoded.backendUserId || decoded.userId || identityId;
        state.accessToken = accessToken;
        state.token = accessToken; // backward compat
        state.email = decoded.email;
        state.username = decoded.username;
        state.role = decoded.role;
        state._id = identityId;
        state.userId = backendUserId;
        state.sessionId = decoded.sessionId || null;
        state.mfaEnabled = decoded.mfaEnabled || false;
        state.error = null;
        state.bootstrapStatus = "ready";
        state.isAuthenticatedResolved = true;

        // clear mfa state after successful login
        state.mfa = {
          required: false,
          token: null,
          methods: [],
        };
      } catch (error) {
        console.error('Failed to decode token in setTokens:', error);
        state.error = "Invalid token";
      }
    },

    // set mfa required state (partial login)
    setMfaRequired(state, action: PayloadAction<MfaRequiredPayload>) {
      state.mfa = {
        required: true,
        token: action.payload.mfaToken,
        methods: action.payload.mfaMethods,
      };
      state.error = null;
    },

    // clear mfa state
    clearMfaState(state) {
      state.mfa = {
        required: false,
        token: null,
        methods: [],
      };
    },

    // update access token (after refresh)
    updateAccessToken(state, action: PayloadAction<string>) {
      const accessToken = action.payload;
      setAuthTokens({ accessToken });

      try {
        const decoded = jwtDecode<DecodedToken>(accessToken);
        const identityId = decoded.sub || decoded._id || state._id || null;
        const backendUserId =
          decoded.backendUserId ||
          decoded.userId ||
          identityId ||
          state.userId;

        state.accessToken = accessToken;
        state.token = accessToken;

        state.email =
          decoded.email !== undefined ? decoded.email ?? null : state.email;
        state.username =
          decoded.username !== undefined ? decoded.username ?? null : state.username;
        state.role =
          typeof decoded.role === "string" && decoded.role.trim().length > 0
            ? decoded.role
            : state.role;
        state._id = identityId;
        state.userId =
          backendUserId !== undefined && backendUserId !== null
            ? String(backendUserId)
            : state.userId;
        state.sessionId = decoded.sessionId || state.sessionId;
        state.mfaEnabled =
          decoded.mfaEnabled !== undefined ? decoded.mfaEnabled : state.mfaEnabled;
        state.error = null;
        state.bootstrapStatus = "ready";
        state.isAuthenticatedResolved = true;

      } catch (error) {
        console.error('Failed to decode token in updateAccessToken:', error);
        state.error = "Invalid token";
      }
    },

    // sign out - clear all auth state
    signOut(state) {
      // clear memory tokens
      clearAuthTokens();
      // clear legacy localStorage keys (if any)
      if (typeof window !== 'undefined') {
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("role");
        localStorage.removeItem("_id");
      }

      state.accessToken = null;
      state.token = null;
      state.email = null;
      state.username = null;
      state.role = null;
      state._id = null;
      state.userId = null;
      state.sessionId = null;
      state.mfa = {
        required: false,
        token: null,
        methods: [],
      };
      state.mfaEnabled = false;
      state.error = null;
      state.isLoading = false;
      state.bootstrapStatus = "ready";
      state.isAuthenticatedResolved = true;
      // refresh token is cleared by backend on logout (HttpOnly cookie)
    },

    setBootstrapStatus(state, action: PayloadAction<"idle" | "loading" | "ready">) {
      state.bootstrapStatus = action.payload;
    },

    setAuthenticatedResolved(state, action: PayloadAction<boolean>) {
      state.isAuthenticatedResolved = action.payload;
    },

    // set auth error
    setAuthError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    // clear auth error
    clearAuthError(state) {
      state.error = null;
    },

    // set loading state
    setAuthLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
});

export const {
  setTokens,
  setMfaRequired,
  clearMfaState,
  updateAccessToken,
  signOut,
  setBootstrapStatus,
  setAuthenticatedResolved,
  setAuthError,
  clearAuthError,
  setAuthLoading,
} = authSlice.actions;

export default authSlice.reducer;
